/*
 * Bonaventure Rental — eSIM order widget (replaces IzyRent for data eSIM).
 *
 * Cart model: one line, unit price = price/day, quantity = number of days.
 * Line properties handed to the backend (gtt-fulfillment) and to the
 * "half price from day 11" discount function:
 *
 *   Date    "YYYY-MM-DD to YYYY-MM-DD"   days = end - start (NOT +1).
 *                                        Matches izyrent.js daysBetween():
 *                                        Aug 10 -> Aug 15 is billed as 5 days.
 *   Days    "5"                          same number, for display/validation.
 *   Country "Thailand"                   GTT destination name, verbatim, only on
 *                                        the multi-destination product. Japan /
 *                                        North America resolve from their SKU.
 *
 * The pure helpers live on window.BvrEsim so they can be unit tested outside a
 * browser (see tests/bvr-esim-order.test.mjs). No DOM work happens at load time
 * when `document` is absent.
 *
 * All user-facing text comes from Liquid via data-strings — never hardcode copy
 * here, it would bypass the locale files.
 */
(function () {
  'use strict';

  var MS_PER_DAY = 86400000;
  var FREE_DAYS = 10; // full price for days 1-10, half price from day 11

  function pad(n, width) {
    var s = String(n);
    while (s.length < width) s = '0' + s;
    return s;
  }

  /*
   * 'YYYY-MM-DD' -> UTC milliseconds, or NaN.
   *
   * Deliberately UTC and deliberately not Date.parse: the calendar day is
   * decided by Liquid in the shop's timezone and handed to us as a plain
   * string, so doing the arithmetic in the browser's local timezone would
   * shift the range for anyone east or west of the shop.
   */
  function parseISO(iso) {
    if (typeof iso !== 'string') return NaN;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return NaN;
    var y = Number(m[1]);
    var mo = Number(m[2]);
    var d = Number(m[3]);
    var t = Date.UTC(y, mo - 1, d);
    var back = new Date(t);
    // Rejects 2026-02-31 and friends, which Date.UTC would silently roll over.
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
      return NaN;
    }
    return t;
  }

  function toISO(ms) {
    var d = new Date(ms);
    return pad(d.getUTCFullYear(), 4) + '-' + pad(d.getUTCMonth() + 1, 2) + '-' + pad(d.getUTCDate(), 2);
  }

  /* Calendar arithmetic that crosses months and years without special cases. */
  function addDays(iso, n) {
    var t = parseISO(iso);
    if (isNaN(t) || typeof n !== 'number' || !isFinite(n) || Math.floor(n) !== n) return null;
    return toISO(t + n * MS_PER_DAY);
  }

  /* The exact string the backend parser expects. */
  function dateRange(startISO, days) {
    if (!isValidDays(days)) return null;
    var end = addDays(startISO, days);
    if (!end) return null;
    return startISO + ' to ' + end;
  }

  function isValidDays(days) {
    return typeof days === 'number' && isFinite(days) && Math.floor(days) === days && days >= 1;
  }

  function clampDays(days, min, max) {
    var n = Math.floor(Number(days));
    if (!isFinite(n)) n = min;
    if (n < min) n = min;
    if (n > max) n = max;
    return n;
  }

  /* ------------------------------------------ country → destination (T2/T3) --- */

  /*
   * The customer picks a COUNTRY; GTT can only be given a DESTINATION.
   *
   * `countryDests` is generated from GTT's own data (gtt-fulfillment/src/gtt-country-map.js)
   * and maps each offered country to `[[startDay, endDay, destination], …]` — "if the trip is
   * this long, order it under this destination". A length in NO range is one GTT has no plan
   * for at that country (Mongolia tops out at 10 days), so it must not be sellable. Everything
   * below is derived from that one table; no country or destination is hard-coded here.
   */
  function destinationFor(country, days, countryDests) {
    if (!country || !countryDests) return null;
    var ranges = countryDests[country];
    if (!Array.isArray(ranges) || !ranges.length) return null;
    if (!isValidDays(days)) return null;
    for (var i = 0; i < ranges.length; i++) {
      if (days >= ranges[i][0] && days <= ranges[i][1]) return ranges[i][2];
    }
    return null;
  }

  /* The shortest and longest trip this country can be sold for, or null when it cannot. */
  function sellableRangeFor(country, countryDests) {
    if (!country || !countryDests) return null;
    var ranges = countryDests[country];
    if (!Array.isArray(ranges) || !ranges.length) return null;
    var min = null, max = null;
    for (var i = 0; i < ranges.length; i++) {
      if (min == null || ranges[i][0] < min) min = ranges[i][0];
      if (max == null || ranges[i][1] > max) max = ranges[i][1];
    }
    return { min: min, max: max };
  }

  /*
   * The next sellable length when stepping by `delta`, skipping the gaps.
   *
   * Gaps are real: a country can be sellable at 10 days and at 14 but not at 11–13, because no
   * plan covering 11 days exists at any destination that serves it. Stepping one at a time
   * would strand the customer on a day they cannot buy, so we jump the hole. Returns the
   * current value when there is nothing further in that direction.
   */
  function nextSellableDays(country, days, delta, countryDests, minDays, maxDays) {
    if (!country || !countryDests || !countryDests[country]) return clampDays(days + delta, minDays, maxDays);
    var step = delta > 0 ? 1 : -1;
    for (var d = days + step; d >= minDays && d <= maxDays; d += step) {
      if (destinationFor(country, d, countryDests)) return d;
    }
    return days;
  }

  /* The nearest sellable length to `days` (used when changing country strands the current one). */
  function snapToSellable(country, days, countryDests, minDays, maxDays) {
    if (!country || !countryDests || !countryDests[country]) return clampDays(days, minDays, maxDays);
    if (destinationFor(country, days, countryDests)) return days;
    var up = nextSellableDays(country, days, 1, countryDests, minDays, maxDays);
    if (destinationFor(country, up, countryDests)) return up;
    var down = nextSellableDays(country, days, -1, countryDests, minDays, maxDays);
    if (destinationFor(country, down, countryDests)) return down;
    return days;
  }

  /* 「現在お取り扱いできない国」— config driven (data/esim-excluded-countries.json), empty by default. */
  function isExcludedCountry(country, excluded) {
    if (!country || !Array.isArray(excluded)) return false;
    for (var i = 0; i < excluded.length; i++) if (excluded[i] === country) return true;
    return false;
  }

  /*
   * Mirrors discount-function/src/run.js exactly:
   *   discount = max(days - 10, 0) * perDay * 0.5
   * Only applied when the product carries the half-price-day11 tag; untagged
   * products are a plain product of days and unit price.
   */
  function lineTotal(days, unitPriceCents, halfPriceEnabled) {
    if (!isValidDays(days)) return 0;
    var full = days * unitPriceCents;
    if (!halfPriceEnabled) return full;
    var discount = Math.max(days - FREE_DAYS, 0) * unitPriceCents * 0.5;
    return Math.round(full - discount);
  }

  function discountAmount(days, unitPriceCents, halfPriceEnabled) {
    if (!halfPriceEnabled || !isValidDays(days)) return 0;
    return Math.round(Math.max(days - FREE_DAYS, 0) * unitPriceCents * 0.5);
  }

  /*
   * Whether the day-11+ discount actually applies to this selection. Both the badge and the
   * "applied at checkout" disclosure hang off this one predicate, so they can never disagree.
   *
   * Why the disclosure exists: the discount is a Shopify Discount Function, which does not run
   * until the cart/checkout — so the cart line still shows days x full price while the widget
   * showed the discounted total. Saying it here turns "the site quoted me less" into an expected
   * step. Below 11 days there is no discount, so neither line is shown.
   */
  function halfPriceApplies(days, halfPriceEnabled) {
    return !!halfPriceEnabled && isValidDays(days) && days > FREE_DAYS;
  }

  /*
   * Minimal money_format renderer. Shopify hands us the shop's format string
   * (e.g. "${{amount}}") in a data attribute; rendering it here keeps the
   * currency symbol out of the JS and out of the locale files.
   */
  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var value = Number(cents) / 100;
    if (!isFinite(value)) value = 0;

    function withSeparators(str, thousands, decimal) {
      var parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_all, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withSeparators(String(Math.round(value)), ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(value.toFixed(2), '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(String(Math.round(value)), '.', ',');
        case 'amount_with_space_separator':
          return withSeparators(value.toFixed(2), ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(String(Math.round(value)), ' ', ',');
        default:
          return withSeparators(value.toFixed(2), ',', '.');
      }
    });
  }

  /* Digits only, for EID/IMEI format checks (customers paste them with spaces/hyphens). */
  function onlyDigits(s) {
    return String(s == null ? '' : s).replace(/\D+/g, '');
  }
  /* IMEI is 15 digits; EID is 32 digits. FORMAT check only — not a Luhn/checksum. */
  function isValidImei(s) {
    return onlyDigits(s).length === 15;
  }
  function isValidEid(s) {
    return onlyDigits(s).length === 32;
  }

  /*
   * ホテル気付で受け取れる国か（WIFI-2）。設定ファイル駆動 = 国を増やすのはデータの変更だけ。
   * 「米国内のホテルに滞在する旅行者」向けの導線なので、対象は当面アメリカだけ。
   */
  function offersHotelDelivery(country, hotelCountries) {
    if (!country || !hotelCountries || !hotelCountries.length) return false;
    for (var i = 0; i < hotelCountries.length; i++) {
      if (String(hotelCountries[i]).toLowerCase() === String(country).toLowerCase()) return true;
    }
    return false;
  }

  /* いまホテル気付モードか（対象国 かつ お客様がホテルを選んでいる）。 */
  function isHotelMode(state) {
    return offersHotelDelivery(state.country, state.hotelCountries) && state.deliveryKind === 'hotel';
  }

  /*
   * Single source of truth for "can this be added to cart". Returns an error
   * KEY, not a message — the caller looks it up in the strings injected by
   * Liquid, so validation stays language-neutral.
   */
  function validate(state) {
    if (state.needsCountry && !state.country) return { ok: false, error: 'country' };
    // 現在お取り扱いできない国（設定ファイル駆動）。選択肢には残すが、カートには入れさせない。
    if (state.needsCountry && isExcludedCountry(state.country, state.excludedCountries)) {
      return { ok: false, error: 'country_excluded' };
    }
    // その国 × その日数を覆う GTT プランが無い = 発注できない。UI 側でも選ばせないが、
    // 直接操作やデータ欠落で抜けてきたときの最後の砦としてここでも止める（黙って売らない）。
    if (state.needsCountry && state.countryDests && state.countryDests[state.country]
        && !destinationFor(state.country, state.days, state.countryDests)) {
      return { ok: false, error: 'days_unavailable' };
    }
    // EID / IMEI are required for number-bearing eSIM (US number): GTT provisions the
    // customer's OWN device, so a missing/malformed value cannot be provisioned.
    if (state.needsEid && !isValidEid(state.eid)) return { ok: false, error: 'eid' };
    if (state.needsEid && !isValidImei(state.imei)) return { ok: false, error: 'imei' };
    if (isNaN(parseISO(state.startDate))) return { ok: false, error: 'date' };
    if (state.minDate && state.startDate < state.minDate) return { ok: false, error: 'date_past' };
    // ── ホテル気付（WIFI-2）─────────────────────────────────────────────
    // 🔴 日付の向きに注意: **開始日 ≥ チェックイン日**。端末はホテルに届くので、
    //    到着していない日に利用が始まる注文を作らせない（司令塔裁定 2026-08-21）。
    //    郵送のリード（今日 + N 日）は**チェックイン日**にかかる — 到着日に間に合わなければ意味がない。
    if (isHotelMode(state)) {
      if (!String(state.hotelName || '').trim()) return { ok: false, error: 'hotel_name' };
      if (isNaN(parseISO(state.checkinDate))) return { ok: false, error: 'checkin' };
      if (state.minDate && state.checkinDate < state.minDate) return { ok: false, error: 'checkin_too_soon' };
      if (state.startDate < state.checkinDate) return { ok: false, error: 'start_before_checkin' };
      if (!String(state.guestName || '').trim()) return { ok: false, error: 'guest_name' };
    }
    if (!isValidDays(state.days) || state.days < state.minDays) return { ok: false, error: 'min_days' };
    if (state.days > state.maxDays) return { ok: false, error: 'max_days' };
    return { ok: true, error: null };
  }

  /* Exactly the payload POSTed to /cart/add.js. Kept pure so the test suite
   * can assert on the properties without a browser. */
  function cartPayload(state) {
    var props = {
      Date: dateRange(state.startDate, state.days),
      Days: String(state.days)
    };
    // 🔴 Country に入れるのは顧客が選んだ国名ではなく、そこから解決した GTT 仕向地。
    //    バックエンド（fulfillment.submitPendingGttOrders）はこの値を GTT POS の仕向地
    //    セレクタに突き合わせて実発注するので、国名のままでは発注できない／別の国を買う。
    //    対応表が無い（= 従来の仕向地選択のまま）ときは選ばれた値をそのまま通す。
    if (state.needsCountry && state.country) {
      var resolved = destinationFor(state.country, state.days, state.countryDests);
      props.Country = resolved || state.country;
    }
    // Send the customer's normalised (digits-only) EID/IMEI for number-bearing eSIM so the
    // backend records exactly what GTT needs to provision (izyrent.js reads EID/IMEI props).
    if (state.needsEid) {
      if (state.eid) props.EID = onlyDigits(state.eid);
      if (state.imei) props.IMEI = onlyDigits(state.imei);
    }
    // ホテル気付の情報は**注文に載せる**（運用がここだけ見れば宛名を書ける）。
    // 住所そのものはチェックアウトで入力してもらう — 当店はチェックアウト画面を改造できないため。
    if (isHotelMode(state)) {
      props.Delivery = 'Hotel';
      props.Hotel = String(state.hotelName).trim();
      props['Check-in'] = state.checkinDate;
      props.Guest = String(state.guestName).trim();
      if (state.hotelAddress) props['Hotel address'] = String(state.hotelAddress).trim();
    } else if (offersHotelDelivery(state.country, state.hotelCountries)) {
      props.Delivery = 'Address';
    }
    return { items: [{ id: state.variantId, quantity: state.days, properties: props }] };
  }

  /*
   * The honest GB/day cap to disclose for {destination, number of days}, or null when none is
   * warranted. Both maps come from Liquid and are auto-derived from the GTT catalog:
   *
   *   byDays      destination → [[startDay, endDay, gb], …]  the cap of the plan we would
   *               ACTUALLY buy for that trip length. Lengths absent from the ranges hold the
   *               quality floor and get no notice (PATCH-2 T3).
   *   worstNotes  destination → gb, the worst case over every sellable length. The fallback.
   *
   * Preferring byDays is what stops a 7-day Worldwide customer being warned with the 0.2GB/day
   * number that only a 61–90 day trip produces (esim-patch1-report §7.3). byDays is treated as
   * authoritative only when it is non-empty: an empty map means it was never generated, and
   * silently dropping a disclosure is worse than over-warning, so that falls back to worstNotes.
   */
  function destNoteGbFor(country, days, byDays, worstNotes) {
    if (!country) return null;
    var haveByDays = byDays && typeof byDays === 'object' && Object.keys(byDays).length > 0;
    if (haveByDays) {
      var ranges = byDays[country];
      if (!Array.isArray(ranges) || !ranges.length) return null;   // never capped, at any length
      if (isValidDays(days)) {
        for (var i = 0; i < ranges.length; i++) {
          if (days >= ranges[i][0] && days <= ranges[i][1]) return ranges[i][2];
        }
        return null;                                               // this length holds the floor
      }
      // No length chosen yet: state the worst case, as before PATCH-2.
      var worst = null;
      for (var j = 0; j < ranges.length; j++) {
        if (worst == null || ranges[j][2] < worst) worst = ranges[j][2];
      }
      return worst;
    }
    if (!worstNotes) return null;
    var gb = worstNotes[country];
    return gb == null ? null : gb;
  }

  /*
   * The factual data-cap notice for a quality-relaxed destination (T6b), rendered. `notes` is the
   * worst-case map and `template` the localised sentence with a __GB__ placeholder. Passing
   * `days` + `byDays` makes it length-aware (T3); without them it is the worst-case notice.
   * Returns '' when no notice is warranted.
   */
  function destNoteFor(country, notes, template, days, byDays) {
    if (!country || !template) return '';
    var gb = destNoteGbFor(country, days, byDays, notes);
    if (gb == null) return '';
    return template.replace('__GB__', String(gb));
  }

  var BvrEsim = {
    FREE_DAYS: FREE_DAYS,
    parseISO: parseISO,
    toISO: toISO,
    addDays: addDays,
    dateRange: dateRange,
    isValidDays: isValidDays,
    clampDays: clampDays,
    lineTotal: lineTotal,
    discountAmount: discountAmount,
    halfPriceApplies: halfPriceApplies,
    formatMoney: formatMoney,
    validate: validate,
    cartPayload: cartPayload,
    onlyDigits: onlyDigits,
    isValidImei: isValidImei,
    isValidEid: isValidEid,
    destNoteGbFor: destNoteGbFor,
    destNoteFor: destNoteFor,
    destinationFor: destinationFor,
    sellableRangeFor: sellableRangeFor,
    nextSellableDays: nextSellableDays,
    snapToSellable: snapToSellable,
    isExcludedCountry: isExcludedCountry,
    offersHotelDelivery: offersHotelDelivery,
    isHotelMode: isHotelMode
  };

  if (typeof window !== 'undefined') window.BvrEsim = BvrEsim;
  if (typeof module !== 'undefined' && module.exports) module.exports = BvrEsim;

  /* ---------------------------------------------------------------- DOM --- */

  if (typeof document === 'undefined') return;

  function initWidget(root) {
    if (root.dataset.bvrEsimReady === '1') return;
    root.dataset.bvrEsimReady = '1';

    var strings = {};
    try {
      strings = JSON.parse(root.getAttribute('data-strings') || '{}');
    } catch (e) {
      strings = {};
    }

    var needsCountry = root.getAttribute('data-needs-country') === 'true';
    var needsEid = root.getAttribute('data-needs-eid') === 'true';
    var halfPrice = root.getAttribute('data-half-price') === 'true';
    var destNotes = {};
    try {
      // JSON lives in a <script type="application/json"> child (element text is robust to the
      // commas/quotes in the map — far safer than an HTML attribute).
      var notesEl = root.querySelector('[data-bvr-dest-notes]');
      destNotes = JSON.parse((notesEl && notesEl.textContent) || '{}');
    } catch (e) {
      destNotes = {};
    }
    var destNotesByDays = {};
    try {
      var byDaysEl = root.querySelector('[data-bvr-dest-notes-by-days]');
      destNotesByDays = JSON.parse((byDaysEl && byDaysEl.textContent) || '{}');
    } catch (e) {
      // An unparseable per-length map must not lose the disclosure: destNoteGbFor falls back
      // to the worst-case map when this one is empty.
      destNotesByDays = {};
    }
    // 国 → 日数ごとの仕向地（T2/T3）。読めなければ空にして、従来どおり選ばれた値をそのまま
    // Country に載せる挙動へ落ちる（表が壊れても注文自体は止めない）。
    var countryDests = {};
    try {
      var cdEl = root.querySelector('[data-bvr-country-dests]');
      countryDests = JSON.parse((cdEl && cdEl.textContent) || '{}');
    } catch (e) {
      countryDests = {};
    }
    var destLabels = {};
    try {
      var dlEl = root.querySelector('[data-bvr-dest-labels]');
      destLabels = JSON.parse((dlEl && dlEl.textContent) || '{}');
    } catch (e) {
      destLabels = {};
    }
    var excludedCountries = [];
    try {
      var exEl = root.querySelector('[data-bvr-excluded-countries]');
      excludedCountries = JSON.parse((exEl && exEl.textContent) || '[]');
    } catch (e) {
      excludedCountries = [];
    }
    var unitPrice = parseInt(root.getAttribute('data-unit-price'), 10) || 0;
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var minDays = parseInt(root.getAttribute('data-min-days'), 10) || 1;
    var maxDays = parseInt(root.getAttribute('data-max-days'), 10) || 90;
    var minDate = root.getAttribute('data-min-date') || '';
    var variantId = root.getAttribute('data-variant-id');
    var cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add.js';
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';

    var countrySelect = root.querySelector('[data-bvr-country]');
    var eidInput = root.querySelector('[data-bvr-eid]');
    var imeiInput = root.querySelector('[data-bvr-imei]');
    var destNoteOutput = root.querySelector('[data-bvr-dest-note]');
    var destAreaOutput = root.querySelector('[data-bvr-dest-area]');
    var excludedOutput = root.querySelector('[data-bvr-excluded]');
    var daysNoteOutput = root.querySelector('[data-bvr-days-note]');
    var dateInput = root.querySelector('[data-bvr-date]');
    var daysOutput = root.querySelector('[data-bvr-days]');
    var minusBtn = root.querySelector('[data-bvr-minus]');
    var plusBtn = root.querySelector('[data-bvr-plus]');
    var totalOutput = root.querySelector('[data-bvr-total]');
    var halfNote = root.querySelector('[data-bvr-half-note]');
    var halfCheckoutNote = root.querySelector('[data-bvr-half-checkout-note]');
    var rangeOutput = root.querySelector('[data-bvr-range]');
    var errorOutput = root.querySelector('[data-bvr-error]');
    var submitBtn = root.querySelector('[data-bvr-submit]');
    // ホテル気付（WIFI-2）。テーマにブロックが無い商品では全て null になり、既存の挙動のまま。
    var hotelBlock = root.querySelector('[data-bvr-hotel-block]');
    var hotelFields = root.querySelector('[data-bvr-hotel-fields]');
    var hotelNameInput = root.querySelector('[data-bvr-hotel-name]');
    var checkinInput = root.querySelector('[data-bvr-checkin]');
    var guestInput = root.querySelector('[data-bvr-guest]');
    var hotelAddrInput = root.querySelector('[data-bvr-hotel-address]');
    var deliveryRadios = root.querySelectorAll('[data-bvr-delivery]');
    // ホテル気付を出す国（設定ファイル駆動）。読めなければ空 = 従来どおりの画面に倒す。
    var hotelCountries = [];
    try {
      var hcEl = root.querySelector('[data-bvr-hotel-countries]');
      hotelCountries = JSON.parse((hcEl && hcEl.textContent) || '[]');
    } catch (e) {
      hotelCountries = [];
    }

    var days = clampDays(minDays, minDays, maxDays);

    function currentState() {
      return {
        needsCountry: needsCountry,
        country: countrySelect ? countrySelect.value : '',
        needsEid: needsEid,
        eid: eidInput ? eidInput.value : '',
        imei: imeiInput ? imeiInput.value : '',
        startDate: dateInput ? dateInput.value : '',
        minDate: minDate,
        days: days,
        minDays: minDays,
        maxDays: maxDays,
        variantId: variantId,
        countryDests: countryDests,
        excludedCountries: excludedCountries,
        hotelCountries: hotelCountries,
        deliveryKind: selectedDelivery(),
        hotelName: hotelNameInput ? hotelNameInput.value : '',
        checkinDate: checkinInput ? checkinInput.value : '',
        guestName: guestInput ? guestInput.value : '',
        hotelAddress: hotelAddrInput ? hotelAddrInput.value : ''
      };
    }

    /* いま選ばれているお届け先の種類（未選択なら 'address' = 現行の挙動）。 */
    function selectedDelivery() {
      for (var i = 0; i < deliveryRadios.length; i++) {
        if (deliveryRadios[i].checked) return deliveryRadios[i].value;
      }
      return 'address';
    }

    /*
     * お届け先の選択を出すかどうか。対象国（いまは米国のみ）を選んだ時だけ出す。
     * 日本を選んでいる間は WIFI-1 と完全に同じ画面 = 既存のお客様の体験を変えない。
     */
    function renderHotel() {
      if (!hotelBlock) return;
      var offer = offersHotelDelivery(countrySelect ? countrySelect.value : '', hotelCountries);
      hotelBlock.hidden = !offer;
      if (!offer) {
        for (var i = 0; i < deliveryRadios.length; i++) deliveryRadios[i].checked = (deliveryRadios[i].value === 'address');
      }
      if (hotelFields) hotelFields.hidden = !(offer && selectedDelivery() === 'hotel');
      // チェックイン日の下限は開始日と同じ（郵送のリード）。上限も揃える。
      if (checkinInput) {
        if (minDate) checkinInput.setAttribute('min', minDate);
        if (dateInput && dateInput.getAttribute('max')) checkinInput.setAttribute('max', dateInput.getAttribute('max'));
      }
    }

    // Show/hide the factual data-cap notice for the selected destination AND the selected
    // number of days (T6b + PATCH-2 T3) — so it must be refreshed by the day stepper too.
    /* The GTT destination the current {country, days} resolves to, or '' before a country is picked. */
    function currentDestination() {
      var country = countrySelect ? countrySelect.value : '';
      if (!country) return '';
      // 対応表が無い（旧来の仕向地セレクタ）ときは選ばれた値そのものが仕向地。
      if (!countryDests[country]) return country;
      return destinationFor(country, days, countryDests) || '';
    }

    // Show/hide the factual data-cap notice for the RESOLVED destination and the selected number
    // of days (T6b + PATCH-2 T3) — so it must be refreshed by the day stepper too. The notes maps
    // are keyed by destination, never by country: the country only decides which destination applies.
    function updateDestNote() {
      if (!destNoteOutput) return;
      var msg = destNoteFor(currentDestination(), destNotes, strings.dest_note, days, destNotesByDays);
      destNoteOutput.textContent = msg;
      destNoteOutput.hidden = !msg;
    }

    /* 解決した通信エリア・対象外表示・その国で売れる日数の幅（T2/T3）。 */
    function updateCountryInfo() {
      var country = countrySelect ? countrySelect.value : '';
      var excluded = isExcludedCountry(country, excludedCountries);

      if (excludedOutput) excludedOutput.hidden = !excluded;

      if (destAreaOutput) {
        var dest = excluded ? '' : currentDestination();
        var label = (dest && destLabels[dest]) || dest;
        var text = dest && strings.dest_area ? strings.dest_area.replace('__AREA__', label) : '';
        destAreaOutput.textContent = text;
        destAreaOutput.hidden = !text;
      }

      if (daysNoteOutput) {
        // 全日数が売れる国では出さない（情報が増えるだけなので）。制限がある国だけ伝える。
        var range = excluded ? null : sellableRangeFor(country, countryDests);
        var limited = range && (range.min > minDays || range.max < maxDays);
        var note = limited && strings.days_limited
          ? strings.days_limited.replace('__MIN__', String(range.min)).replace('__MAX__', String(range.max))
          : '';
        daysNoteOutput.textContent = note;
        daysNoteOutput.hidden = !note;
      }
    }

    // Last-resort text so a failure can NEVER be silent, even if the locale
    // strings failed to parse. Silent add-to-cart failures are the single worst
    // failure mode for this widget, so visibility is not allowed to depend on
    // anything that can itself break.
    var LAST_RESORT = 'Could not add to cart. Please try again.';

    function showError(key) {
      if (!errorOutput) return;
      if (!key) {
        errorOutput.textContent = '';
        errorOutput.hidden = true;
        return;
      }
      errorOutput.textContent = strings[key] || strings.generic || LAST_RESORT;
      errorOutput.hidden = false;
    }

    // Show a message the server gave us verbatim (Shopify localises these), or
    // fall back to the generic string. Never renders empty.
    function showMessage(text) {
      if (!errorOutput) return;
      errorOutput.textContent = text || strings.generic || LAST_RESORT;
      errorOutput.hidden = false;
    }

    function render() {
      renderHotel();   // ホテル気付の表示切替（対象国を選んだ時だけ出す）
      if (daysOutput) daysOutput.textContent = String(days);
      // 売れる日数の端では、まだ数字が動かせるように見えて動かないボタンを押させない（T3）。
      var country = countrySelect ? countrySelect.value : '';
      var sellable = sellableRangeFor(country, countryDests);
      var lowest = sellable ? Math.max(minDays, sellable.min) : minDays;
      var highest = sellable ? Math.min(maxDays, sellable.max) : maxDays;
      if (minusBtn) minusBtn.disabled = days <= lowest;
      if (plusBtn) plusBtn.disabled = days >= highest;

      var total = lineTotal(days, unitPrice, halfPrice);
      if (totalOutput) totalOutput.textContent = formatMoney(total, moneyFormat);
      var discounted = halfPriceApplies(days, halfPrice);
      if (halfNote) halfNote.hidden = !discounted;
      if (halfCheckoutNote) halfCheckoutNote.hidden = !discounted;

      var state = currentState();
      if (rangeOutput) {
        var range = dateRange(state.startDate, days);
        rangeOutput.textContent = range || '';
        rangeOutput.hidden = !range;
      }
      // The cap depends on the length, not just the destination (T3).
      updateCountryInfo();
      updateDestNote();
      if (submitBtn) submitBtn.disabled = false;
    }

    function step(delta) {
      var country = countrySelect ? countrySelect.value : '';
      days = countryDests[country]
        ? nextSellableDays(country, days, delta, countryDests, minDays, maxDays)
        : clampDays(days + delta, minDays, maxDays);
      showError(null);
      render();
    }

    if (minusBtn) minusBtn.addEventListener('click', function () { step(-1); });
    if (plusBtn) plusBtn.addEventListener('click', function () { step(1); });
    if (dateInput) dateInput.addEventListener('change', function () { showError(null); render(); });
    if (countrySelect) {
      countrySelect.addEventListener('change', function () {
        // 国を変えたとき、いまの日数がその国では売れないことがある（例: 30日でモンゴルに変更）。
        // 買えない日数のまま黙って残さず、その国で売れる最も近い日数へ寄せる。
        var picked = countrySelect.value;
        if (countryDests[picked]) days = snapToSellable(picked, days, countryDests, minDays, maxDays);
        showError(null);
        render();
      });
    }
    for (var di = 0; di < deliveryRadios.length; di++) {
      deliveryRadios[di].addEventListener('change', function () { showError(null); render(); });
    }
    if (hotelNameInput) hotelNameInput.addEventListener('input', function () { showError(null); });
    if (guestInput) guestInput.addEventListener('input', function () { showError(null); });
    if (checkinInput) checkinInput.addEventListener('change', function () { showError(null); render(); });
    if (eidInput) eidInput.addEventListener('input', function () { showError(null); });
    if (imeiInput) imeiInput.addEventListener('input', function () { showError(null); });

    if (submitBtn) {
      submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        var state = currentState();
        var check = validate(state);
        if (!check.ok) {
          showError(check.error);
          return;
        }
        // A missing variant id means /cart/add.js can only fail. Catch it here
        // with a clear message instead of letting an empty id reach the server.
        if (!state.variantId) {
          console.error('[bvr-esim] no variant id on the widget — cannot add to cart');
          showMessage(strings.generic || LAST_RESORT);
          return;
        }

        showError(null);
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.disabled = true;

        function reenable() {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.disabled = false;
        }

        // Read the body defensively: a non-.js endpoint or a redirect returns
        // HTML, and response.json() would throw on it. We keep the raw text so
        // the real failure is logged instead of swallowed.
        fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(cartPayload(state))
        })
          .then(function (response) {
            return response.text().then(function (raw) {
              var body = null;
              try {
                body = JSON.parse(raw);
              } catch (e) {
                body = null;
              }
              if (!response.ok || !body) {
                var detail = (body && (body.description || body.message)) || ('HTTP ' + response.status);
                console.error('[bvr-esim] add to cart failed:', response.status, raw.slice(0, 300));
                var err = new Error(detail);
                err.serverMessage = body && body.description ? body.description : null;
                throw err;
              }
              return body;
            });
          })
          .then(function () {
            window.location.href = cartUrl;
          })
          .catch(function (error) {
            console.error('[bvr-esim]', error);
            // Prefer Shopify's own localised description when it gave us one.
            showMessage(error && error.serverMessage ? error.serverMessage : strings.generic);
            reenable();
          });
      });
    }

    render();   // renders the total, the day bounds, the notes and the destination notice
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-bvr-esim]');
    for (var i = 0; i < nodes.length; i++) initWidget(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', initAll);
})();
